import React, { useState, useEffect, useMemo } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import './MembersPage.css'; // reuse table/filter styles

const ALL = 'All';
const TYPE_FILTERS = [ALL, 'Members', 'Coupons', 'Events', 'Notifications'];

const _months = ['Jan','Feb','Mar','Apr','May','Jun',
                 'Jul','Aug','Sep','Oct','Nov','Dec'];

function tsToDate(ts) {
  return ts?.toDate?.() ?? null;
}

function fmtWhen(d) {
  if (!d) return '—';
  const now = new Date();
  const diff = now - d;
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'yesterday';
  if (days < 7)   return `${days}d ago`;
  return `${d.getDate()} ${_months[d.getMonth()]} ${d.getFullYear()}`;
}

function fmtAbsolute(d) {
  if (!d) return '';
  const hh = d.getHours().toString().padStart(2, '0');
  const mm = d.getMinutes().toString().padStart(2, '0');
  return `${d.getDate()} ${_months[d.getMonth()]} · ${hh}:${mm}`;
}

const TYPE_META = {
  member:       { label: 'Member',       color: '#22C55E', icon: '👤' },
  coupon:       { label: 'Coupon',       color: '#D4B574', icon: '🏷️' },
  event:        { label: 'Event',        color: '#3B82F6', icon: '📅' },
  notification: { label: 'Notification', color: '#A855F7', icon: '🔔' },
  promo:        { label: 'Promo Popup',  color: '#E63946', icon: '★' },
};

export default function LogsPage() {
  const [members,       setMembers]       = useState([]);
  const [coupons,       setCoupons]       = useState([]);
  const [events,        setEvents]        = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [search,        setSearch]        = useState('');
  const [typeF,         setTypeF]         = useState(ALL);

  useEffect(() => {
    let pending = 4;
    const done = () => { if (--pending === 0) setLoading(false); };

    const u1 = onSnapshot(collection(db, 'allowed_users'),
      s => { setMembers(s.docs.map(d => ({ id: d.id, ...d.data() }))); done(); },
      () => done());
    const u2 = onSnapshot(collection(db, 'coupons'),
      s => { setCoupons(s.docs.map(d => ({ id: d.id, ...d.data() }))); done(); },
      () => done());
    const u3 = onSnapshot(collection(db, 'events'),
      s => { setEvents(s.docs.map(d => ({ id: d.id, ...d.data() }))); done(); },
      () => done());
    const u4 = onSnapshot(collection(db, 'notifications'),
      s => { setNotifications(s.docs.map(d => ({ id: d.id, ...d.data() }))); done(); },
      () => done());
    return () => { u1(); u2(); u3(); u4(); };
  }, []);

  // Merge all into one timeline.
  const allEvents = useMemo(() => {
    const out = [];
    for (const m of members) {
      out.push({
        type: 'member',
        when: tsToDate(m.joined_on),
        title: `New member: ${m.name || '—'}`,
        sub:   `${m.phone_number || ''} · ${m.district || ''} · ${m.member_id || ''}`,
        id: `member-${m.id}`,
      });
    }
    for (const c of coupons) {
      out.push({
        type: 'coupon',
        when: tsToDate(c.created_at),
        title: `Coupon published: ${c.title || '—'}`,
        sub:   `${c.partner || ''} · code ${c.code || ''} · ${c.discount || ''}`,
        id: `coupon-${c.id}`,
      });
    }
    for (const ev of events) {
      out.push({
        type: 'event',
        when: tsToDate(ev.created_at),
        title: `Event created: ${ev.title || '—'}`,
        sub:   `${ev.district || ''} · ${ev.date || ''} ${ev.time || ''}`,
        id: `event-${ev.id}`,
      });
    }
    for (const n of notifications) {
      const isPromo = n.type === 'promo';
      out.push({
        type: isPromo ? 'promo' : 'notification',
        when: tsToDate(n.sentAt),
        title: isPromo
          ? `Promo popup sent: ${n.title || '(image-only)'}`
          : `Notification sent: ${n.title || '—'}`,
        sub:  `→ ${n.target === 'all' ? 'all members' : n.target}`,
        id: `notif-${n.id}`,
      });
    }
    // Sort by date desc; entries without a date fall to the bottom.
    out.sort((a, b) => {
      if (!a.when && !b.when) return 0;
      if (!a.when) return 1;
      if (!b.when) return -1;
      return b.when - a.when;
    });
    return out;
  }, [members, coupons, events, notifications]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allEvents.filter(ev => {
      const matchSearch = !q
        || ev.title?.toLowerCase().includes(q)
        || ev.sub?.toLowerCase().includes(q);
      const matchType = typeF === ALL
        || (typeF === 'Members'       && ev.type === 'member')
        || (typeF === 'Coupons'       && ev.type === 'coupon')
        || (typeF === 'Events'        && ev.type === 'event')
        || (typeF === 'Notifications' && (ev.type === 'notification' || ev.type === 'promo'));
      return matchSearch && matchType;
    });
  }, [allEvents, search, typeF]);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Activity Logs</h1>
          <p className="page-subtitle">
            {allEvents.length} total events ·{' '}
            Live-updating timeline of everything that's been created or sent.
          </p>
        </div>
      </div>

      <div className="mem-filters">
        <input
          type="search"
          className="mem-search"
          placeholder="Search logs…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          className="mem-select"
          value={typeF}
          onChange={e => setTypeF(e.target.value)}
        >
          {TYPE_FILTERS.map(t => <option key={t}>{t}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="mem-loading">
          <span className="tf-spinner tf-spinner--red" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="mem-empty">No log entries match this filter.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map(ev => {
            const meta = TYPE_META[ev.type];
            return (
              <div
                key={ev.id}
                style={{
                  background: '#161618',
                  border: '1px solid #1FD4B574',
                  borderRadius: 10,
                  padding: '12px 14px',
                  display: 'flex',
                  gap: 12,
                  alignItems: 'center',
                }}
              >
                <div style={{
                  width: 36, height: 36,
                  borderRadius: 8,
                  background: `${meta.color}26`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18,
                  flexShrink: 0,
                }}>
                  {meta.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    marginBottom: 2,
                  }}>
                    <span style={{
                      padding: '1px 6px',
                      background: `${meta.color}22`,
                      color: meta.color,
                      borderRadius: 3,
                      fontSize: 9,
                      fontWeight: 700,
                      letterSpacing: 1,
                      textTransform: 'uppercase',
                    }}>
                      {meta.label}
                    </span>
                    <span style={{ color: '#B79F6D', fontSize: 11 }}>
                      {fmtWhen(ev.when)}
                      {ev.when && ` · ${fmtAbsolute(ev.when)}`}
                    </span>
                  </div>
                  <div style={{
                    color: '#F7E8BF',
                    fontSize: 13,
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {ev.title}
                  </div>
                  {ev.sub && (
                    <div style={{
                      color: '#9A9A9A',
                      fontSize: 11,
                      marginTop: 2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {ev.sub}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
